<script>
import AoikumoDisabledTooltip from 'dashboard/components/dahreply/AoikumoDisabledTooltip.vue';

export default {
  name: 'ConditionCard',
  components: {
    AoikumoDisabledTooltip,
  },
  props: {
    conditionData: {
      type: Object,
      required: true,
      validator: (c) => {
        return c.id && c.condition && c.treatment_suggestion !== undefined;
      }
    },
    loading: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isHovered: false
    };
  },
  computed: {
    conditionId() {
      return this.conditionData.id;
    },
    conditionName() {
      return this.conditionData.condition;
    },
    treatment() {
      return this.conditionData.treatment_suggestion;
    },
    images() {
      return this.conditionData.images || [];
    },
    hasEmbedding() {
      return this.conditionData.has_embedding;
    },
    updatedAt() {
      return this.conditionData.updated_at;
    },
    cardClasses() {
      return {
        'opacity-50 cursor-not-allowed pointer-events-none': false,
        'cursor-pointer hover:shadow-lg hover:border-slate-300 dark:hover:border-slate-600': true,
        'animate-pulse': this.loading
      };
    },
    ariaLabel() {
      return `${this.conditionName} condition`;
    }
  },
  methods: {
    onEdit(event) {
      if (this.loading || this.readOnly) return;
      event.stopPropagation();
      this.$emit('edit', this.conditionData);
    },
    onDelete(event) {
      if (this.loading || this.readOnly) return;
      event.stopPropagation();
      this.$emit('delete', {
        name: this.conditionName,
        id: this.conditionId
      });
    },
    onNavigate() {
      if (this.loading) return;
      this.$emit('navigate', this.conditionId);
    },
    onMouseEnter() {
      if (!this.loading) this.isHovered = true;
    },
    onMouseLeave() {
      this.isHovered = false;
    }
  },
  emits: ['edit', 'delete', 'navigate']
};
</script>

<template>
  <div
    class="relative bg-white dark:bg-slate-800 dark:text-white p-5 rounded-lg shadow-md border border-slate-200 dark:border-slate-700 transition-all duration-200"
    :class="cardClasses"
    @click="onNavigate"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    :aria-label="ariaLabel"
    role="button"
    tabindex="0"
    @keydown.enter="onNavigate"
    @keydown.space="onNavigate"
  >
    <!-- Loading overlay -->
    <div
      v-if="loading"
      class="absolute inset-0 bg-white dark:bg-slate-800 bg-opacity-75 rounded-lg flex items-center justify-center z-10"
    >
      <fluent-icon icon="spinner" class="animate-spin h-6 w-6 text-primary" />
    </div>

    <!-- Hover Actions -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <AoikumoDisabledTooltip
        v-if="isHovered && !loading"
        :disabled="readOnly"
        class="absolute top-3 right-3 flex gap-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg p-1 z-20"
        role="toolbar"
        aria-label="Condition actions"
      >
        <!-- Edit -->
        <button
          :disabled="readOnly"
          @click.stop="onEdit"
          class="p-2 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors duration-150 focus:outline-none"
          aria-label="Edit condition"
          title="Edit"
        >
          <fluent-icon icon="edit" class="h-4 w-4" />
        </button>

        <!-- Delete -->
        <button
          :disabled="readOnly"
          @click.stop="onDelete"
          class="p-2 rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 transition-colors duration-150 focus:outline-none"
          aria-label="Delete condition"
          title="Delete"
        >
          <fluent-icon icon="delete" class="h-4 w-4" />
        </button>
      </AoikumoDisabledTooltip>
    </transition>

    <!-- Static action dot -->
    <div v-if="!isHovered && !loading" class="absolute top-3 right-3 opacity-60">
      <fluent-icon icon="more-horizontal" class="h-5 w-5 text-slate-400" />
    </div>

    <!-- Content -->
    <div class="flex items-start gap-4">
      <!-- Image Gallery or Icon -->
      <div class="flex-shrink-0">
        <div 
          v-if="images.length > 0"
          class="relative h-16 w-16 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700"
        >
          <img
            :src="images[0]"
            :alt="`${conditionName} condition image`"
            class="h-full w-full object-cover"
          />
          <div 
            v-if="images.length > 1"
            class="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center"
          >
            <span class="text-white text-xs font-medium">+{{ images.length - 1 }}</span>
          </div>
        </div>
        <div 
          v-else
          class="h-16 w-16 rounded-lg bg-primary-50 dark:bg-primary-900 flex items-center justify-center"
        >
          <fluent-icon 
            icon="stethoscope" 
            class="h-6 w-6 text-primary"
          />
        </div>
      </div>

      <div class="min-w-0 flex-1">
        <h4 
          class="text-lg font-semibold text-slate-800 dark:text-white truncate"
          :title="conditionName"
        >
          {{ conditionName }}
        </h4>

        <p class="text-sm text-slate-600 dark:text-slate-300 mt-1 line-clamp-2">
          {{ treatment }}
        </p>

        <!-- Embedding status -->
        <div class="mt-3 flex items-center gap-2">
          <span
            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
            :class="hasEmbedding
              ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'"
          >
            <fluent-icon 
              :icon="hasEmbedding ? 'checkmark-circle' : 'warning'" 
              class="h-3 w-3 mr-1" 
            />
            {{ hasEmbedding ? 'Embedding Ready' : 'Embedding Missing' }}
          </span>
        </div>

        <!-- Images badge -->
        <div class="mt-2" v-if="images.length > 0">
          <span class="text-xs text-slate-500 dark:text-slate-400">
            {{ images.length }} image{{ images.length > 1 ? 's' : '' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

[role="button"]:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

button {
  min-width: 2rem;
  min-height: 2rem;
}
</style>